import { useQuery } from '@tanstack/react-query'
import { Link, useSearchParams } from 'react-router-dom'
import { api } from '../api/client'
import type { DeliveryBoardItemOut, DeliveryBoardOut, DeliveryBoardRunOut } from '../api/types'
import { Kpi } from '../components/Kpi'
import { PageHeader } from '../components/Layout'
import { Panel } from '../components/States'
import { StatusChip } from '../components/StatusChip'
import { isoDate } from '../lib/dates'
import { count, duration, formatDate, formatTime, humanise, percent } from '../lib/format'

/** Live runs first, finished ones last. */
const RUN_ORDER = ['in_progress', 'assigned', 'pending_assignment', 'completed']

function shiftDay(day: string, by: number): string {
  const date = new Date(`${day}T00:00:00`)
  date.setDate(date.getDate() + by)
  return isoDate(date)
}

export function DeliveriesTodayPage() {
  const [params, setParams] = useSearchParams()
  const today = isoDate(new Date())
  const day = params.get('date') || today

  function setDay(next: string) {
    const updated = new URLSearchParams(params)
    if (next && next !== today) updated.set('date', next)
    else updated.delete('date')
    setParams(updated, { replace: true })
  }

  // Drivers finish runs from their phones through the day; a minute's lag is
  // fine for a board, and nothing is fetched while the tab is hidden.
  const board = useQuery({
    queryKey: ['delivery-board', day],
    queryFn: () => api.get<DeliveryBoardOut>('/delivery/board', { date: day }),
    refetchInterval: 60_000,
  })

  const data = board.data

  return (
    <>
      <PageHeader
        title={day === today ? "Today's deliveries" : 'Deliveries'}
        subtitle={
          <>
            {formatDate(day)}
            {board.dataUpdatedAt ? (
              <> · updated {formatTime(new Date(board.dataUpdatedAt).toISOString())}, refreshes every minute</>
            ) : null}
          </>
        }
      >
        <div className="row" style={{ alignItems: 'flex-end' }}>
          <button className="btn" onClick={() => setDay(shiftDay(day, -1))} aria-label="Previous day">
            ←
          </button>
          <input
            type="date"
            aria-label="Delivery date"
            value={day}
            onChange={(event) => setDay(event.target.value)}
          />
          <button className="btn" onClick={() => setDay(shiftDay(day, 1))} aria-label="Next day">
            →
          </button>
          {day !== today ? (
            <button className="btn ghost" onClick={() => setDay(today)}>
              Today
            </button>
          ) : null}
        </div>
      </PageHeader>

      <div className="content stack">
        <Panel isLoading={board.isPending} error={board.error}>
          {data ? <BoardView data={data} /> : null}
        </Panel>
      </div>
    </>
  )
}

/**
 * The board itself — KPIs and one card per run — for the live page and the
 * demo. `demo` turns off links into records that don't exist.
 */
export function BoardView({
  data,
  demo = false,
  now,
}: {
  data: DeliveryBoardOut
  demo?: boolean
  /** The demo's simulated clock; the live board measures against real time. */
  now?: Date
}) {
  const kpis = data.kpis
  const runs = [...data.runs].sort((a, b) => RUN_ORDER.indexOf(a.status) - RUN_ORDER.indexOf(b.status))
  const attempted = kpis.items_delivered + kpis.items_partial + kpis.items_undelivered

  return (
    <>
      <div className="grid cols-4">
        <Kpi
          label="Runs"
          value={count(kpis.runs_total)}
          foot={[
            `${kpis.runs_completed} done`,
            `${kpis.runs_in_progress} on the road`,
            `${kpis.runs_not_started} not started`,
            kpis.runs_needing_driver ? `${kpis.runs_needing_driver} need a driver` : null,
          ]
            .filter(Boolean)
            .join(' · ')}
        />
        <Kpi
          label="Items"
          value={count(kpis.items_total)}
          foot={`${count(kpis.items_pending)} still to deliver`}
        />
        <Kpi
          label="Delivered"
          value={attempted ? percent((kpis.items_delivered / attempted) * 100, 0) : '—'}
          foot={
            attempted
              ? `${kpis.items_delivered} delivered · ${kpis.items_partial} partial · ${kpis.items_undelivered} failed`
              : 'No outcomes recorded yet'
          }
        />
        <Kpi
          label="Inbox backlog"
          to={demo ? undefined : '/deliveries/inbox'}
          value={count(kpis.backlog_items)}
          foot={
            kpis.backlog_oldest_days != null
              ? `Unscheduled, all dates · oldest ${kpis.backlog_oldest_days} day${kpis.backlog_oldest_days === 1 ? '' : 's'}`
              : 'Unscheduled, all dates'
          }
        />
      </div>

      {runs.length === 0 ? (
        <div className="card">
          <div className="card-body">
            <p className="hint">No delivery runs are scheduled for {formatDate(data.date)}.</p>
          </div>
        </div>
      ) : (
        runs.map((run) => <RunCard key={run.id} run={run} demo={demo} now={now} />)
      )}
    </>
  )
}

function RunCard({ run, demo, now }: { run: DeliveryBoardRunOut; demo: boolean; now?: Date }) {
  const done = run.items.filter((item) => item.outcome).length
  const failed = run.items.filter((item) => item.outcome === 'undelivered').length
  const partial = run.items.filter((item) => item.outcome === 'partial').length

  let timing = 'Not started'
  if (run.status === 'pending_assignment') timing = 'Waiting for a driver'
  else if (run.started_at && run.completed_at)
    timing = `${formatTime(run.started_at)} – ${formatTime(run.completed_at)} · ${duration(run.started_at, run.completed_at)}`
  else if (run.started_at) timing = `Out since ${formatTime(run.started_at)} · ${duration(run.started_at, now?.toISOString())}`

  return (
    <div className="card">
      <div className="card-head">
        <h2>{run.drivers.length ? run.drivers.join(', ') : 'No driver yet'}</h2>
        <div className="row" style={{ alignItems: 'center', gap: 8 }}>
          {failed ? <span className="chip red">{failed} failed</span> : null}
          {partial ? <span className="chip amber">{partial} partial</span> : null}
          <StatusChip status={run.status} />
        </div>
      </div>
      <div className="card-body stack">
        <div className="row" style={{ alignItems: 'center', gap: 12 }}>
          <span className="hint">{timing}</span>
          <div className="bar" style={{ flex: 1, maxWidth: 240 }}>
            <span style={{ width: `${run.items.length ? (done / run.items.length) * 100 : 0}%` }} />
          </div>
          <span className="hint">
            {done} of {run.items.length} items
          </span>
          {run.recipient_name ? <span className="hint">· received by {run.recipient_name}</span> : null}
        </div>
        {run.notes ? <div className="alert info">{run.notes}</div> : null}
      </div>
      <div className="card-body flush">
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Invoice</th>
                <th>Customer</th>
                <th>Parish</th>
                <th>Item</th>
                <th className="num">Qty</th>
                <th>Outcome</th>
              </tr>
            </thead>
            <tbody>
              {run.items.map((item) => (
                <ItemRow key={item.id} item={item} demo={demo} />
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

function ItemRow({ item, demo }: { item: DeliveryBoardItemOut; demo: boolean }) {
  return (
    <tr>
      <td>
        {demo ? (
          <span className="strong">{item.invoice_reference}</span>
        ) : (
          <Link to={`/invoices/${item.invoice_id}`}>{item.invoice_reference ?? '—'}</Link>
        )}
        {item.previously_attempted ? (
          <div>
            <span className="chip orange">Re-attempt</span>
          </div>
        ) : null}
      </td>
      <td>
        {item.customer_name || '—'}
        {item.delivery_address ? <div className="cell-sub">{item.delivery_address}</div> : null}
      </td>
      <td className="cell-sub">{item.delivery_parish ?? '—'}</td>
      <td>
        {item.item_name ?? '—'}
        {item.item_model ? <div className="cell-sub">{item.item_model}</div> : null}
      </td>
      <td className="num">{count(item.qty)}</td>
      <td>
        <StatusChip status={item.outcome ?? item.status} />
        {item.reason ? <div className="cell-sub">{humanise(item.reason)}</div> : null}
        {item.reason_notes ? <div className="cell-sub">{item.reason_notes}</div> : null}
      </td>
    </tr>
  )
}
